import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Plus, Trash2, Save, ArrowLeft, Minus, Search } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { PageHeader, Panel } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { useVariants } from "../components/VariantPicker";

let _rid = 0;

export default function TransferCreate() {
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = location.state || null;
  const { flat, products } = useVariants();
  const [warehouses, setWarehouses] = useState([]);
  const [from, setFrom] = useState(prefill?.from || "");
  const [to, setTo] = useState(prefill?.to || "");
  const [rows, setRows] = useState(() => (prefill?.items || []).map((it) => ({ key: ++_rid, ...it })));
  const [saving, setSaving] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  useEffect(() => { api.get("/warehouses", { params: { usage: "produk_jadi" } }).then((r) => setWarehouses(r.data)).catch(() => {}); }, []);

  const setRow = (key, patch) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const delRow = (key) => setRows((rs) => rs.filter((r) => r.key !== key));
  const openAdd = () => { if (!from) return toast.error("Pilih Transfer From dulu"); setAddOpen(true); };
  const addVariants = (items) => setRows((rs) => [...rs, ...items.map((it) => ({ key: ++_rid, ...it }))]);

  const save = async () => {
    if (!from || !to) return toast.error("Pilih Transfer From & Transfer To");
    if (from === to) return toast.error("Gudang asal & tujuan tidak boleh sama");
    const items = rows.filter((r) => r.variant_id && (parseInt(r.qty) || 0) > 0).map((r) => ({
      product_id: (flat.find((f) => f.variant_id === r.variant_id) || {}).product_id,
      variant_id: r.variant_id, product_name: r.product_name, color: r.color, size: r.size,
      qc_status: "passed", qty: parseInt(r.qty) || 0,
    }));
    if (!items.length) return toast.error("Tambahkan minimal 1 produk dengan qty");
    setSaving(true);
    try {
      await api.post("/transfers/quick", { from_warehouse: from, to_warehouse: to, notes: "Transfer stok manual", items });
      toast.success("Transfer berhasil disimpan");
      navigate("/transfers");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="transfer-create-page">
      <PageHeader title="Form Transfer Stock" subtitle="Buat transfer stok antar gudang">
        <Button variant="outline" onClick={() => navigate("/transfers")} className="border-taupe text-cocoa" data-testid="back-btn"><ArrowLeft className="w-4 h-4 mr-1.5" />Kembali</Button>
      </PageHeader>

      <Panel className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
          <div><Label>Transfer From</Label>
            <Select value={from || undefined} onValueChange={setFrom}>
              <SelectTrigger className="bg-white" data-testid="create-from-select"><SelectValue placeholder="Pilih gudang asal" /></SelectTrigger>
              <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Transfer To</Label>
            <Select value={to || undefined} onValueChange={setTo}>
              <SelectTrigger className="bg-white" data-testid="create-to-select"><SelectValue placeholder="Pilih gudang tujuan" /></SelectTrigger>
              <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div className="border border-taupe/20 rounded-lg overflow-visible">
          <div className="grid grid-cols-[40px_2fr_1fr_1fr_100px_120px_90px_44px] items-center px-3 py-2 bg-cocoa/5 text-xs font-bold text-cocoa uppercase tracking-wide">
            <div>No</div><div>Product Name</div><div>Color</div><div>Size</div><div className="text-center">Max Stock</div><div>Keep Status</div><div className="text-center">Qty</div><div></div>
          </div>
          {rows.map((r, i) => (
            <ProductRow key={r.key} row={r} index={i} onQty={(q) => setRow(r.key, { qty: q })} onRemove={() => delRow(r.key)} />
          ))}
          {!rows.length && <div className="py-8 text-center text-wmuted text-sm" data-testid="transfer-empty">Belum ada produk. Klik "Add Product" untuk menambah varian.</div>}
          <div className="flex justify-center py-4 border-t border-taupe/15">
            <Button onClick={openAdd} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-product-btn"><Plus className="w-4 h-4 mr-1.5" />Add Product</Button>
          </div>
        </div>

        <div className="flex justify-end mt-5">
          <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-transfer-btn"><Save className="w-4 h-4 mr-1.5" />{saving ? "Menyimpan..." : "Save Transfer"}</Button>
        </div>
      </Panel>

      <AddProductDialog open={addOpen} onOpenChange={setAddOpen} from={from} products={products}
        existingIds={rows.map((r) => r.variant_id)} onAdd={addVariants} />
    </div>
  );
}

function ProductRow({ row, index, onQty, onRemove }) {
  const maxN = row.max == null ? null : parseInt(row.max);
  return (
    <div className="grid grid-cols-[40px_2fr_1fr_1fr_100px_120px_90px_44px] items-center px-3 py-2 border-t border-taupe/10 text-sm" data-testid={`transfer-row-${index}`}>
      <div className="text-wmuted">{index + 1}</div>
      <div className="font-medium text-cocoa truncate pr-2">{row.product_name}</div>
      <div className="text-cocoa">{row.color || "-"}</div>
      <div className="text-cocoa">{row.size || "-"}</div>
      <div className="text-center font-semibold text-cocoa">{row.max == null ? "-" : row.max}</div>
      <div>{row.keep > 0 ? <span className="text-[11px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Keep: {row.keep}</span> : <span className="text-wmuted text-xs">Tidak ada</span>}</div>
      <div className="text-center">
        <Input type="number" min={1} max={maxN ?? undefined} value={row.qty}
          onChange={(e) => { let v = parseInt(e.target.value) || 1; if (maxN != null) v = Math.min(v, Math.max(1, maxN)); onQty(Math.max(1, v)); }}
          className="h-8 w-16 text-center mx-auto" data-testid={`qty-${index}`} />
      </div>
      <div className="text-center"><button onClick={onRemove} className="text-status-reject" data-testid={`remove-row-${index}`}><Trash2 className="w-4 h-4" /></button></div>
    </div>
  );
}

function AddProductDialog({ open, onOpenChange, from, products, existingIds, onAdd }) {
  const [q, setQ] = useState("");
  const [product, setProduct] = useState(null);
  const [vrows, setVrows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (!open) { setQ(""); setProduct(null); setVrows([]); } }, [open]);

  const matches = q.trim() && !product
    ? products.filter((p) => (p.name || "").toLowerCase().includes(q.trim().toLowerCase())).slice(0, 25)
    : [];

  const pickProduct = async (p) => {
    setProduct(p); setQ(p.name); setLoading(true);
    try {
      const r = await api.get("/transfers/product-variants", { params: { product_id: p.id, warehouse: from } });
      setVrows((r.data.variants || []).map((v) => ({ ...v, qty: 0 })));
    } catch { toast.error("Gagal memuat stok varian"); setVrows([]); }
    finally { setLoading(false); }
  };

  const clamp = (v, mx) => Math.max(0, Math.min(parseInt(v) || 0, mx));
  const setQty = (vid, val) => setVrows((rs) => rs.map((r) => (r.variant_id === vid ? { ...r, qty: clamp(val, r.available) } : r)));
  const step = (vid, d) => setVrows((rs) => rs.map((r) => (r.variant_id === vid ? { ...r, qty: clamp((parseInt(r.qty) || 0) + d, r.available) } : r)));

  const save = () => {
    const chosen = vrows.filter((r) => (parseInt(r.qty) || 0) > 0);
    if (!chosen.length) return toast.error("Isi qty minimal 1 varian");
    const fresh = chosen.filter((r) => !existingIds.includes(r.variant_id));
    const dup = chosen.length - fresh.length;
    if (fresh.length) {
      onAdd(fresh.map((r) => ({ variant_id: r.variant_id, product_name: product.name, color: r.color, size: r.size, max: r.available, keep: r.keep, qty: parseInt(r.qty) })));
      toast.success(`${fresh.length} varian ditambahkan`);
    }
    if (dup) toast.info(`${dup} varian sudah ada, dilewati`);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="add-product-dialog">
        <DialogHeader><DialogTitle className="font-heading text-cocoa">Add Product</DialogTitle></DialogHeader>
        <div>
          <Label>Product</Label>
          <div className="relative">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-wmuted" />
              <Input value={q} onChange={(e) => { setQ(e.target.value); setProduct(null); setVrows([]); }}
                placeholder="Cari nama produk..." className="h-10 bg-white pl-9" data-testid="addprod-search" />
            </div>
            {matches.length > 0 && (
              <div className="absolute z-40 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-taupe/30 rounded-md shadow-lg" data-testid="addprod-options">
                {matches.map((p) => (
                  <button key={p.id} className="block w-full text-left px-3 py-2 text-sm hover:bg-cocoa/5"
                    onClick={() => pickProduct(p)} data-testid={`addprod-opt-${p.id}`}>{p.name}</button>
                ))}
              </div>
            )}
          </div>
        </div>

        {loading && <div className="py-10 text-center text-wmuted">Memuat varian...</div>}
        {!loading && product && (
          <div className="mt-2 border border-taupe/20 rounded-lg overflow-hidden">
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent">
                <TableHead>Color</TableHead><TableHead>Size</TableHead>
                <TableHead className="text-center">Max Stock</TableHead><TableHead className="text-center">Action</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {vrows.map((v) => (
                  <TableRow key={v.variant_id} data-testid={`addprod-var-${v.variant_id}`}>
                    <TableCell className="text-cocoa">{v.color}{v.keep > 0 && <span className="ml-1.5 text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">Keep {v.keep}</span>}</TableCell>
                    <TableCell className="text-cocoa">{v.size}</TableCell>
                    <TableCell className="text-center font-semibold text-cocoa">{v.available}</TableCell>
                    <TableCell>
                      <div className="flex items-center justify-center gap-1.5">
                        <Button type="button" size="icon" variant="outline" className="h-8 w-8 border-taupe" disabled={v.available === 0} onClick={() => step(v.variant_id, -1)} data-testid={`addprod-minus-${v.variant_id}`}><Minus className="w-3.5 h-3.5" /></Button>
                        <Input type="number" min={0} max={v.available} value={v.qty} onChange={(e) => setQty(v.variant_id, e.target.value)}
                          className="h-8 w-16 text-center" disabled={v.available === 0} data-testid={`addprod-qty-${v.variant_id}`} />
                        <Button type="button" size="icon" variant="outline" className="h-8 w-8 border-taupe" disabled={v.available === 0} onClick={() => step(v.variant_id, 1)} data-testid={`addprod-plus-${v.variant_id}`}><Plus className="w-3.5 h-3.5" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {!vrows.length && <TableRow><TableCell colSpan={4} className="text-center text-wmuted py-6 text-sm">Produk ini tidak punya varian.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        )}
        {!loading && !product && <div className="py-10 text-center text-wmuted text-sm">Cari & pilih produk untuk melihat daftar varian.</div>}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-taupe text-cocoa" data-testid="addprod-cancel">Cancel</Button>
          <Button onClick={save} disabled={!product} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="addprod-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
